import { useRef, useState } from "react"
import { useSWRConfig } from "swr"
import { ConfigPropertiesPanel, type ConfigProperty, type ConfigPropertyValue } from "@/components/alfred/config-properties-panel"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { ApiErrorState } from "@/components/shared/empty-states"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useSmithersGlobal, useUpdateSmithersGlobal } from "@/hooks/use-smithers-global"
import { useProVenueInstances } from "@/hooks/use-provenue-instances"
import { useToast } from "@/hooks/use-toast"
import { toSmithersGlobalRequest } from "@/lib/smithers-global-request"
import { formatDateTimeWithZone } from "@/lib/utils"

export function SmithersGlobalPage() {
  const global = useSmithersGlobal()
  const update = useUpdateSmithersGlobal()
  const instances = useProVenueInstances("smithers")
  const { mutate } = useSWRConfig()
  const { toast } = useToast()
  const [saveError, setSaveError] = useState<string>()
  const submitting = useRef(false)
  const config = global.data
  const options = [{ value: "none", label: "None" }, ...(instances.data ?? []).map((instance) => ({ value: String(instance.id), label: instance.name }))]
  if (config?.proVenueInstanceId != null && !instances.data?.some((item) => item.id === config.proVenueInstanceId)) {
    options.push({ value: String(config.proVenueInstanceId), label: `${config.proVenueUser || "Unavailable instance"} (${config.proVenueInstanceId})` })
  }
  const instanceName = (values: Readonly<Record<string, ConfigPropertyValue>>) => {
    const id = values.proVenueInstanceId
    if (id === "none") return "None"
    return instances.data?.find((instance) => instance.id === Number(id))?.name
      ?? (config && Number(id) === config.proVenueInstanceId ? config.proVenueUser : "Unavailable instance")
  }
  const instanceHost = (values: Readonly<Record<string, ConfigPropertyValue>>) => {
    const id = values.proVenueInstanceId
    if (id === "none") return "—"
    return instances.data?.find((instance) => instance.id === Number(id))?.hostName
      ?? (config && Number(id) === config.proVenueInstanceId ? config.proVenueInstances[0]?.hostName : undefined) ?? "—"
  }
  const properties: ConfigProperty[] = config ? [
    { key: "validationVendor", label: "Validation Vendor", section: "Device Configuration", type: "enum", value: config.validationVendor, options: [{ value: "ProVenue", label: "ProVenue" }, { value: "Orion", label: "Orion" }] },
    ...([ ["facilityCode", "Facility Code"], ["venueId", "Venue ID"], ["areaCode", "Area Code"], ["deviceCode", "Device Code"], ["nteClubCode", "NTE Club Code"] ] as const).map(([key, label]): ConfigProperty => ({ key, label, section: "Device Configuration", type: "string", value: config[key] })),
    { key: "matchThreshold", label: "Match Threshold", section: "Device Configuration", type: "number", value: config.matchThreshold, min: 0, max: 2147483647, step: 1 },
    { key: "validationTimeoutMs", label: "Validation Timeout (ms)", section: "Device Configuration", type: "number", value: config.validationTimeoutMs, min: 0, max: Number.MAX_SAFE_INTEGER, step: 1 },
    { key: "proVenueInstanceId", label: "ProVenue Instance", section: "ProVenue Connection", type: "enum", value: config.proVenueInstanceId === null ? "none" : String(config.proVenueInstanceId), options },
    { key: "proVenueUser", label: "ProVenue User", section: "ProVenue Connection", type: "readonly", value: config.proVenueUser || "None", deriveValue: instanceName },
    { key: "proVenueHost", label: "ProVenue Host", section: "ProVenue Connection", type: "readonly", value: config.proVenueInstances[0]?.hostName || "—", deriveValue: instanceHost },
    { key: "updatedAt", label: "Updated At", section: "Audit", type: "readonly", value: formatDateTimeWithZone(config.updatedAt) },
    { key: "updatedBy", label: "Updated By", section: "Audit", type: "readonly", value: config.updatedBy },
  ] : []

  const save = async (values: Record<string, ConfigPropertyValue | null>) => {
    if (submitting.current) return
    submitting.current = true
    setSaveError(undefined)
    try {
      await update.trigger(toSmithersGlobalRequest(values), { populateCache: true, revalidate: false })
      await mutate((key) => Array.isArray(key) && key[0] === "smithers" && key[1] === "devices", undefined, { revalidate: true })
      toast({ variant: "success", title: "Global defaults updated", description: "Devices inheriting changed values will update in the background." })
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to save global defaults"
      setSaveError(message)
      toast({ variant: "error", title: "Failed to update global defaults", description: message })
      throw error
    } finally { submitting.current = false }
  }

  return <>
    <DashboardHeader />
    <main className="container mx-auto flex flex-col gap-6 px-4 py-6">
      <div><h1 className="mb-2 text-2xl font-semibold text-foreground">Smithers Global Defaults</h1><p className="text-sm text-muted-foreground">These values are inherited by devices without their own overrides.</p></div>
      {global.isLoading ? <Skeleton className="h-64 w-full" aria-label="Loading global defaults" /> : global.error || !config ? <ApiErrorState resourceName="global defaults" onRetry={() => void global.mutate()} /> : <>
        {saveError && <Alert variant="destructive"><AlertTitle>Couldn’t save global defaults</AlertTitle><AlertDescription>{saveError}</AlertDescription></Alert>}
        {instances.isLoading && <p role="status">Loading ProVenue instances…</p>}
        {instances.error && <Alert variant="destructive"><AlertTitle>Couldn’t load ProVenue instances</AlertTitle><AlertDescription>You can keep the current selection or choose None. <Button type="button" variant="outline" onClick={() => void instances.mutate()}>Retry instances</Button></AlertDescription></Alert>}
        <ConfigPropertiesPanel className="mx-0" properties={properties} resetKey={config} onSave={save} isSaving={update.isMutating} showUseDefault={false} configurationTitle="Global configuration" saveConfirmationTitle="Save global configuration changes?" saveConfirmationDescription="This change affects devices inheriting these values. Device updates will run in the background." />
      </>}
    </main>
  </>
}
